<template>
  <div
    class="checkout-container"
    :style="{
      'background-image': `url(${require('@/assets/lw-order-process-background-min.jpg')})`,
    }"
  >
    <template v-if="!loading">
      <ProgressBar :steps="checkoutSteps" :current-step="currentCheckoutStep" />

      <div class="checkout">
        <div class="checkout-form">
          <router-view :key="routePath" />
        </div>
        <router-view
          name="summary"
          :key="routePath"
          :title="summaryTitle"
          :nickname="getNickname"
          :loading="loadingSummary"
          :package="selectedPackage"
          :logo="summaryLogo"
          :app-name="summaryAppName"
          :location="selectedLocation"
          :addons="addons"
          :term="packageTerm"
          :coupon="coupon"
          :discount="discount"
          :tax="tax"
          :total="total"
          :storebuilder="storebuilder"
        />
      </div>
    </template>

    <div class="loader center-middle" v-if="loading">
      <LoadingState :size="50" />
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import constants from "@/utils/constants";
import LoadingState from "@/components/display/LoadingState";
import ProgressBar from "@/components/display/ProgressBar";
import store from "@/store";

export default {
  name: "CheckoutPage",
  components: {
    LoadingState,
    ProgressBar,
  },
  props: {
    appId: Number,
    packageId: Number,
    locationId: Number,
    term: String,
    couponCode: String,
    nickname: String,
    installApp: Boolean,
    affiliateId: Number,
    addonId: Number,
    hasBigCommerce: Boolean,
    storeBuilderId: String,
  },
  setup(props, context) {
    const requireStoreBuilder = computed(() => store.getters["checkout/requireStoreBuilder"]);
    const storebuilder = computed(() => store.getters["checkout/requireStoreBuilderUi"]);
    const routePath = computed(() => context.root.$route.fullPath);
    const checkoutSteps = computed(() => {
      if (requireStoreBuilder.value) {
        return storebuilder
          ? constants.CHECKOUT_STOREBUILDER_UI_OVERRIDE_STEPS
          : constants.CHECKOUT_STOREBUILDER_STEPS;
      }
      return constants.CHECKOUT_STEPS;
    });
    const currentCheckoutStep = computed(() => context.root.$route.name);
    const selectedApp = computed(() =>
      store.getters["apps/getAppById"](store.state.checkout.appId)
    );
    const selectedPackage = computed(() =>
      store.getters["appPackages/getAppPackage"](
        store.state.checkout.packageId,
        store.state.checkout.appId
      )
    );
    const selectedLocation = computed(() =>
      store.getters["locations/getLocationById"](store.getters["checkout/getLocationId"])
    );
    const addons = computed(() => store.getters["checkout/getAddons"](selectedPackage.value));
    const taxStatus = computed(() => store.getters["checkout/getTaxStatus"]);
    const getNickname = computed(() => store.getters["checkout/getNickname"]);
    const packageTerm = computed(() => store.getters["checkout/getTerm"]);
    const coupon = computed(() => store.getters["checkout/getCouponCode"]);
    const discount = computed(() => store.getters["checkout/getDiscount"]);
    const tax = computed(() => store.getters["checkout/getTax"]);
    const total = computed(() => store.getters["checkout/getTotal"](selectedPackage.value));
    const billingAddress = computed(() => store.getters["addresses/getBillingAddress"]);
    const summaryTitle = computed(() => {
      if (!selectedPackage.value || !selectedApp.value) {
        return "";
      }
      return storebuilder.value
        ? `${selectedPackage.value.name}`
        : `${selectedApp.value.name} ${selectedPackage.value.name}`;
    });
    const summaryLogo = computed(() =>
      storebuilder.value ? "storebuilder" : selectedApp.value?.type
    );
    const summaryAppName = computed(() => selectedApp.value?.name);
    const loadingSummary = computed(
      () => !selectedPackage.value || !selectedApp.value || taxStatus.value.isLoading
    );
    const loading = ref(true);
    const calculateDiscount = () => {
      // if a coupon has been set (from query string) validate it now
      if (props.appId && props.packageId && props.couponCode) {
        const amount = store.getters["checkout/getPackageFee"](selectedPackage.value);
        store
          .dispatch("discounts/validateCoupon", {
            packageId: props.packageId,
            appId: props.appId,
            amount,
            couponCode: props.couponCode,
            term: props.term,
          })
          .then((discount) => {
            store.dispatch("checkout/setDiscount", discount);
          })
          .catch(() => {
            store.dispatch("checkout/setCouponCode", null);
          });
      }
    };

    onMounted(() => {
      store
        .dispatch("settings/fetch")
        .then(() => {
          if (!store.getters["auth/getToken"]) return Promise.resolve();
          return store.dispatch("auth/userClientInfo", { reidentify: true });
        })
        .catch((e) => e)
        .finally(() => {
          const resourceList = [];

          if (props.appId) {
            resourceList.push(store.dispatch("apps/getList"));
          }

          if (props.packageId) {
            resourceList.push(store.dispatch("appPackages/getList"));
          }

          if (props.locationId) {
            resourceList.push(store.dispatch("locations/getList"));
          }

          Promise.all(resourceList)
            .then(() => {
              store.dispatch("checkout/setApp", props.appId);
              store.dispatch("checkout/setPackage", props.packageId);
              store.dispatch("checkout/setTerm", props.term);
              store.dispatch("checkout/addAddon", props.addonId);
              store.dispatch("checkout/autoRedirect", props.appId && props.packageId);
              store.dispatch("checkout/setLocation", props.locationId);
              store.dispatch("checkout/setNickname", props.nickname);
              store.dispatch("checkout/setAffiliate", props.affiliateId);
              store.dispatch("checkout/setInstallApp", props.installApp);
              store.dispatch("checkout/setHasBigCommerce", props.hasBigCommerce);
              store.dispatch("checkout/setStoreBuilderId", props.storeBuilderId);
              store.dispatch("checkout/setCouponCode", props.couponCode);
              store.dispatch("checkout/cartstackActive", true);

              // if user is logged in, load addresses (including billing address)
              if (store.getters["auth/isLoggedIn"]) {
                const user = store.getters["auth/getUser"];
                store.dispatch("cartstack/setEmail", user.email);
                store
                  .dispatch("addresses/getList")
                  .then(() => {
                    // if billing address is present, try to calculate taxes
                    if (billingAddress.value) {
                      store.dispatch("checkout/calculateTax");
                    }
                  })
                  .catch((e) => console.log(e));
              }

              calculateDiscount();

              // temporarily redirect just in case use gets to this page with a free trial package id
              // once marketing links get updated, this can go away
              if (selectedPackage.value?.isFreeTrial) {
                if (store.getters["auth/isLoggedIn"]) {
                  store.dispatch("checkout/setPackage", null);
                } else {
                  // continue to the profile step
                  context.root.$router.push({
                    name: "checkout-free-trial-details",
                    query: store.getters["checkout/getQueryParams"],
                  });
                }
              }
            })
            .finally(() => {
              loading.value = false;
            });
        });
    });

    return {
      loading,
      routePath,
      checkoutSteps,
      currentCheckoutStep,
      selectedApp,
      selectedPackage,
      selectedLocation,
      addons,
      packageTerm,
      coupon,
      discount,
      tax,
      total,
      summaryTitle,
      summaryLogo,
      summaryAppName,
      loadingSummary,
      getNickname,
      storebuilder,
    };
  },
};
</script>

<style lang="scss">
@import "~@/styles/variables";

.checkout-container {
  // background-image: require("@/assets/lw-order-process-background-min.jpg");
  background-size: cover;
  height: 100%;
  overflow: auto;
  padding-top: 1.5rem;
  // position: relative;
  // top: 0;
}
</style>
